import type { RootState, SetState } from '../store';
import type { PopupType } from '@/app/components/popup/constants';
import type { LogOutPopData } from '@/types/aggregation/common';
import type { PrismicObject } from '@/types/prismic/common';
import { POPUPS } from '@/app/components/popup/constants';

type PopupParams = Record<string, unknown>;

type PopupQueueItem = {
    id: string;
    type: PopupType;
    message: string;
    params: PopupParams;
};

type QueuePopupArgs = {
    type: PopupType;
    message?: string;
    params?: PopupParams;
};

export type PopupSliceProps = {
    popup: {
        queue: PopupQueueItem[];
        activePopup: PopupQueueItem | undefined;
        logoutPopupData: PrismicObject<'logout_popup', LogOutPopData> | undefined;

        queuePopup: (args: QueuePopupArgs) => void;
        updatePopupParams: (id: string, params: PopupParams) => void;
        closePopup: () => void;
        clearPopups: () => void;
        removePopupByType: (type: PopupType) => void;
        onCloseAction: null | (() => void);
        setOnCloseAction: (onCloseAction: () => void) => void;
        setLogoutPopupData: (logoutPopupData: PrismicObject<'logout_popup', LogOutPopData> | undefined) => void;
    };
};

const POPUP_PRIORITY: Record<PopupType, number> = {
    [POPUPS.STATE_SELECTOR]: 80,
    [POPUPS.MOMENTUM_LOYALTY]: 60,
    [POPUPS.LOGOUT]: 100,
    [POPUPS.EXCLUSIVE_ZONE]: 70,
    [POPUPS.GAME_INFO]: 50,
    [POPUPS.SWJ_ERROR]: 90,
    [POPUPS.SWJ_OPT_IN]: 40,
    [POPUPS.DYB_INTERSTITIAL]: 65,
    [POPUPS.VOLATILITY_INFO]: 52,
    [POPUPS.BIGGEST_WIN]: 55,
    [POPUPS.REWARD_MACHINE_WELCOME_MODAL]: 50
};

export const popupSlice = ({ set }: SetState<RootState>): PopupSliceProps => ({
    popup: {
        queue: [],
        activePopup: undefined,
        logoutPopupData: undefined,

        queuePopup: ({ type, message = '', params = {} }: QueuePopupArgs) =>
            set(
                (state) => {
                    const id = crypto.randomUUID();
                    const newItem: PopupQueueItem = { id, type, message, params };

                    const queue = [...state.popup.queue, newItem];

                    queue.sort((a, b) => {
                        const pa = POPUP_PRIORITY[a.type] ?? 0;
                        const pb = POPUP_PRIORITY[b.type] ?? 0;
                        return pb - pa;
                    });

                    return {
                        popup: {
                            ...state.popup,
                            queue,
                            activePopup: queue[0]
                        }
                    };
                },
                false,
                'popup/queuePopup'
            ),
        onCloseAction: null,
        setOnCloseAction: (onCloseAction: () => void) =>
            set(
                (state) => ({
                    ...state,
                    popup: {
                        ...state.popup,
                        onCloseAction: onCloseAction
                    }
                }),
                false,
                'popup/setOnCloseAction'
            ),

        updatePopupParams: (id: string, params: PopupParams) =>
            set(
                (state) => {
                    const queue = state.popup.queue.map((item) =>
                        item.id === id ? { ...item, params: { ...item.params, ...params } } : item
                    );

                    return {
                        popup: {
                            ...state.popup,
                            queue,
                            activePopup: queue[0]
                        }
                    };
                },
                false,
                'popup/updatePopupParams'
            ),

        closePopup: () =>
            set(
                (state) => {
                    const [active, ...rest] = state.popup.queue;
                    if (!active) return state;

                    return {
                        popup: {
                            ...state.popup,
                            queue: rest,
                            activePopup: rest[0],
                            // Reset the one-shot onCloseAction with the popup that owned it
                            // so a stale callback from one flow (e.g. SwitchRegionButton)
                            // cannot leak into a later, unrelated State Selector usage
                            // (e.g. login/register triggering the selector without
                            // setting its own onCloseAction).
                            onCloseAction: null
                        }
                    };
                },
                false,
                'popup/closePopup'
            ),

        clearPopups: () =>
            set(
                (state) => ({
                    popup: {
                        ...state.popup,
                        queue: [],
                        activePopup: undefined,
                        onCloseAction: null
                    }
                }),
                false,
                'popup/clearPopups'
            ),

        removePopupByType: (type) =>
            set(
                (state) => {
                    const remaining = state.popup.queue.filter((item) => item.type !== type);

                    return {
                        popup: {
                            ...state.popup,
                            queue: remaining,
                            activePopup: remaining[0]
                        }
                    };
                },
                false,
                'popup/removePopupByType'
            ),

        setLogoutPopupData: (logoutPopupData: PrismicObject<'logout_popup', LogOutPopData> | undefined) =>
            set(
                (state) => ({
                    popup: {
                        ...state.popup,
                        logoutPopupData
                    }
                }),
                false,
                'popup/setLogoutPopup'
            )
    }
});
